/**
 * The risk score's bands and orderings — how the score is grouped and shown, not how it is produced.
 *
 * `risk-from-scorecard.ts` produces it: the mean of a repository's threat-model scorecard dimensions,
 * inverted into an uncovered-posture gap and scaled by how exposed the repository is. So these bands
 * group a **real derived number**, and this module is presentation over it.
 *
 * It began as the presentation half of the ISS-8362 prototype, alongside a fabricated `RepositoryRisk`
 * payload — schemas in `api-schemas.ts`'s utility region and a hook in `api-hooks.ts` — that stood in for
 * an endpoint nobody served. ISS-8786 replaced that invention with the scorecard derivation and deleted
 * both. This third piece outlived them because bands and orderings were never the fabricated part.
 */

/**
 * The score's five bands, worst first.
 *
 * **Bands are deliberately unnamed.** Calling them Critical/High/Medium/Low would borrow the severity
 * vocabulary for a different scale: a finding's severity and a repository's risk score are measured
 * differently and do not convert into one another, and sharing words invites readers to assume they do.
 * The range *is* the label, everywhere the bands are shown.
 *
 * Bands are even twenties rather than a school curve. The score is real but the *cutoffs* are ours: it
 * is a posture gap scaled by exposure, not a measurement calibrated against outcomes, so uneven bands
 * would claim a distribution nothing has established. Both ends are inclusive.
 *
 * The colour each band's arc is drawn in lives in `repo-risk-donut.css.ts` — it is presentation rather
 * than payload, and keeping it there keeps this module free of any `@pixee/design-system` import, which
 * the Playwright fixtures that import this file cannot resolve.
 */
const SAFEST_BAND = { id: '0-19', minScore: 0, maxScore: 19 } as const

export const RISK_BANDS = [
  { id: '80-100', minScore: 80, maxScore: 100 },
  { id: '60-79', minScore: 60, maxScore: 79 },
  { id: '40-59', minScore: 40, maxScore: 59 },
  { id: '20-39', minScore: 20, maxScore: 39 },
  SAFEST_BAND,
] as const

export type RiskBand = (typeof RISK_BANDS)[number]
export type RiskBandId = RiskBand['id']

/** The band's range as it is shown to a reader — an en dash, not the hyphen the id uses. */
export const riskBandLabel = (band: RiskBand): string => `${band.minScore}–${band.maxScore}`

/** Returns the band a risk score falls in. Scores outside 0–100 clamp to the nearest end. */
export const bandForScore = (riskScore: number): RiskBand =>
  // Only a negative score misses every band, and the safest one is the right answer for that too.
  RISK_BANDS.find(band => riskScore >= band.minScore) ?? SAFEST_BAND
